import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { ApiError, apiGet } from "../../../lib/api.js";

/**
 * B-GTCC-A18 — register row 9 ("Smoke Dashboard inkl. Testdaten-Seeding"),
 * the reading half only.
 *
 * Measured, not assumed:
 *
 *   1. There is no smoke endpoint and no persisted run history. A repo
 *      wide search for a `/smoke` route finds none; the gtcc1 dashboard
 *      composes its picture out of eight ordinary admin GETs
 *      (`apps/admin/src/smoke/SmokeDashboard.tsx:24-29`). So "letzte
 *      Laeufe" cannot be read from the server — the run IS the page
 *      load, and the only honest timestamp is the moment each answer
 *      arrived in this browser.
 *
 *   2. All eight probes sit behind `requirePlatformOwner(...)`, so the
 *      whole page has one reach and one 403 story, unlike the device
 *      approval view where queue and audit differ.
 *
 *   3. Seeding is a write (`POST /v1/admin/staging/seed-test-data`) and
 *      lives in the view (`GtccSmokeView.tsx`, SeedingCard, E-GTCC-9-SEED1)
 *      behind a confirmation step; this hook stays reads only, so the
 *      eight probes and the one write never share a code path.
 */

/** The probes this view runs, in display order. */
export const GTCC_SMOKE_PROBES = [
  "tenants",
  "pending",
  "pool",
  "sims",
  "rejections",
  "mail",
  "email_delivery",
  "customer_launch",
] as const;

export type GtccSmokeProbeKey = (typeof GTCC_SMOKE_PROBES)[number];

/** Page size for the email delivery probe; the route caps at 200. */
export const GTCC_SMOKE_EMAIL_PAGE_SIZE = 50;

export type GtccSmokeTone = "green" | "yellow" | "red";

export interface GtccSmokeItemList {
  readonly items?: readonly unknown[];
}

export interface GtccSmokeRejections {
  readonly counts?: Record<string, number>;
}

export interface GtccSmokeMailHealth {
  readonly provider?: "log-only" | "smtp" | "postmark";
}

export interface GtccSmokeEmailDelivery {
  readonly items?: readonly { readonly status: string }[];
  readonly total?: number;
}

export interface GtccSmokeLaunchOverview {
  readonly items?: readonly { readonly overall: string }[];
  readonly total?: number;
  readonly capped?: boolean;
}

/** `/v1/admin/tenants` answers `{ items }`; older builds answered a bare array. */
export function countItems(data: GtccSmokeItemList | readonly unknown[] | undefined): number {
  if (Array.isArray(data)) return data.length;
  return (data as GtccSmokeItemList | undefined)?.items?.length ?? 0;
}

export function sumCounts(data: GtccSmokeRejections | undefined): number {
  const counts = data?.counts;
  if (!counts) return 0;
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

/**
 * Postmark is the transport production is wired to; smtp and log-only
 * are legitimate elsewhere but must not read as "all good" here.
 */
export function mailTone(provider: string | undefined): GtccSmokeTone {
  return provider === "postmark" ? "green" : "yellow";
}

const FAILED_DELIVERY_STATES = ["failed", "rejected", "bounced", "spam_complaint"];

export function failedDeliveries(items: readonly { readonly status: string }[]): number {
  return items.filter((row) => FAILED_DELIVERY_STATES.includes(row.status)).length;
}

/**
 * An empty log is not a regression — a fresh install has simply not
 * sent anything yet. Red only when failures dominate the page we read.
 */
export function deliveryTone(total: number, shown: number, failed: number): GtccSmokeTone {
  if (total > 0 && failed > 0 && failed >= shown / 2) return "red";
  return "green";
}

export interface GtccLaunchCounts {
  readonly ready: number;
  readonly needs_action: number;
  readonly blocked: number;
  readonly manual: number;
}

export function launchCounts(
  items: readonly { readonly overall: string }[] = [],
): GtccLaunchCounts {
  const counts = { ready: 0, needs_action: 0, blocked: 0, manual: 0 };
  for (const item of items) {
    if (item.overall === "ready") counts.ready += 1;
    else if (item.overall === "needs_action") counts.needs_action += 1;
    else if (item.overall === "blocked") counts.blocked += 1;
    else counts.manual += 1;
  }
  return counts;
}

/**
 * Capped answers stay yellow: a blocked tenant beyond the first page is
 * invisible here, and hiding that behind green would be a lie.
 */
export function launchTone(counts: GtccLaunchCounts, capped: boolean): GtccSmokeTone {
  if (counts.blocked > 0) return "red";
  if (counts.needs_action > 0 || counts.manual > 0 || capped) return "yellow";
  return "green";
}

export function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** The tone of one successful probe answer — the same reading the table row uses. */
export function probeTone(key: GtccSmokeProbeKey, queries: GtccSmokeQueries): GtccSmokeTone {
  switch (key) {
    case "tenants":
    case "pending":
    case "pool":
    case "sims":
      return "green";
    case "rejections":
      return "green";
    case "mail":
      return mailTone(queries.mail.data?.provider);
    case "email_delivery": {
      const data = queries.email_delivery.data;
      const items = data?.items ?? [];
      return deliveryTone(data?.total ?? 0, items.length, failedDeliveries(items));
    }
    case "customer_launch": {
      const data = queries.customer_launch.data;
      const items = data?.items ?? [];
      return launchTone(launchCounts(items), data?.capped ?? false);
    }
  }
}

export interface GtccSmokeTally {
  readonly green: number;
  readonly yellow: number;
  readonly red: number;
  readonly pending: number;
}

/** Rolls every probe up into one tally, the same reading `GtccSmokeView`'s table gives per row. */
export function tallySmokeQueries(queries: GtccSmokeQueries): GtccSmokeTally {
  return GTCC_SMOKE_PROBES.reduce<GtccSmokeTally>(
    (acc, key) => {
      const query = queries[key];
      if (query.isPending) return { ...acc, pending: acc.pending + 1 };
      if (query.isError) return { ...acc, red: acc.red + 1 };
      const tone = probeTone(key, queries);
      return { ...acc, [tone]: acc[tone] + 1 };
    },
    { green: 0, yellow: 0, red: 0, pending: 0 },
  );
}

const STALE_TIME_MS = 30_000;

function useSmokeQuery<T>(
  key: GtccSmokeProbeKey,
  path: string,
  params?: Record<string, string | number>,
  enabled = true,
): UseQueryResult<T, Error> {
  return useQuery<T, Error>({
    queryKey: ["gtcc", "smoke", key],
    queryFn: ({ signal }) => apiGet<T>(path, params, signal),
    staleTime: STALE_TIME_MS,
    retry: false,
    enabled,
  });
}

export interface GtccSmokeQueries {
  readonly tenants: UseQueryResult<GtccSmokeItemList | readonly unknown[], Error>;
  readonly pending: UseQueryResult<GtccSmokeItemList, Error>;
  readonly pool: UseQueryResult<GtccSmokeItemList, Error>;
  readonly sims: UseQueryResult<GtccSmokeItemList, Error>;
  readonly rejections: UseQueryResult<GtccSmokeRejections, Error>;
  readonly mail: UseQueryResult<GtccSmokeMailHealth, Error>;
  readonly email_delivery: UseQueryResult<GtccSmokeEmailDelivery, Error>;
  readonly customer_launch: UseQueryResult<GtccSmokeLaunchOverview, Error>;
}

/** Every probe is a GET; nothing here writes. */
export function useGtccSmokeQueries(enabled = true): GtccSmokeQueries {
  return {
    tenants: useSmokeQuery<GtccSmokeItemList | readonly unknown[]>(
      "tenants",
      "/v1/admin/tenants",
      undefined,
      enabled,
    ),
    pending: useSmokeQuery<GtccSmokeItemList>(
      "pending",
      "/v1/admin/devices/pending",
      undefined,
      enabled,
    ),
    pool: useSmokeQuery<GtccSmokeItemList>(
      "pool",
      "/v1/admin/managed-hardware/pool",
      undefined,
      enabled,
    ),
    sims: useSmokeQuery<GtccSmokeItemList>("sims", "/v1/admin/sims", undefined, enabled),
    rejections: useSmokeQuery<GtccSmokeRejections>(
      "rejections",
      "/v1/admin/gateway/rejections",
      undefined,
      enabled,
    ),
    mail: useSmokeQuery<GtccSmokeMailHealth>("mail", "/v1/admin/mail/health", undefined, enabled),
    email_delivery: useSmokeQuery<GtccSmokeEmailDelivery>(
      "email_delivery",
      "/v1/admin/email-deliveries",
      { limit: GTCC_SMOKE_EMAIL_PAGE_SIZE },
      enabled,
    ),
    customer_launch: useSmokeQuery<GtccSmokeLaunchOverview>(
      "customer_launch",
      "/v1/admin/customer-launch-overview",
      undefined,
      enabled,
    ),
  };
}
